import { Button, Dropdown } from 'antd';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { useContext, useMemo, useRef, useState } from 'react';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import { calculateContainerHeight, formatNumWithKMBAlt, getConfig, scaleMaxValue } from 'utils/helperMethods';
import DownSvg from 'assets/svg/Down';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import { isEmpty, startCase } from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import useDecompositionChartData from './hooks/useDecompositionChartData';
import { groupBarChartWidth } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import DecompositionWaterfallChart from './DecompositionWaterfallChart';
import useDecompositionWaterfallData from './hooks/useDecompositionWaterfallData';

const DecompositionChart = ({ selectedBar }: { selectedBar: string }) => {
    const chartHeight = calculateContainerHeight(2.3);

    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultAttributeForData } = profitPoolConfig;
    const { allDimAndAttributeTypeList } = useContext(FilterContextData);

    const containerWidth = useContainerWidth({
        containerId: 'decomposition-chart-infinite-scroll',
    });

    const graphViewContainerWidth = Math.max(16 * groupBarChartWidth, containerWidth) + 300;

    const chartRef = useRef<any>(null);
    const [selectedDecompositionLevel, setSelectedDecompositionLevel] = useState<typeof dim1AttributeList[0] | null>(
        defaultAttributeForData,
    );

    const selectedDecompositionChartType = useMemo(() => {
        if (selectedBar === 'CORONA FAMILY') {
            return 'waterfall';
        } else {
            return 'bar';
        }
    }, [selectedBar]);

    const dim1AttributeList = useMemo(
        () =>
            allDimAndAttributeTypeList?.dimentions[0]?.attribute.map((_item: any) => {
                return {
                    ..._item,
                    key: _item.name,
                    dataKey: _item.name,
                    label: startCase(_item.name),
                    dimension: _item?.dimensionReference,
                };
            }),
        [allDimAndAttributeTypeList],
    );

    const {
        decompositionChartData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        min,
        max,
    } = useDecompositionChartData(selectedDecompositionLevel);

    const {
        isPending: isWaterfallPending,
        isError: isWaterfallError,
        isSuccess: isWaterfallSuccess,
        waterFallChartData: decompositionWaterfallData,
    } = useDecompositionWaterfallData(selectedDecompositionLevel);

    if (isPending || isWaterfallPending) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }

    if (isError || isWaterfallError) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </div>
        );
    }

    if (isEmpty(decompositionChartData) || isEmpty(decompositionWaterfallData)) {
        return (
            <div>
                <p className="text-lg font-semibold">{'Decomposition'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </div>
        );
    }

    console.log('decompositionChartData', decompositionChartData);
    return (
        <>
            <div className="flex flex-row items-center justify-between mb-6">
                <div className="flex flex-row gap-x-4">
                    <p className="text-lg font-semibold">{'Decomposition by'}</p>
                    <Dropdown
                        menu={{
                            items: dim1AttributeList,
                            onClick: (info) => {
                                setSelectedDecompositionLevel(
                                    dim1AttributeList.find((_item: any) => _item.key === info.key) || null,
                                );
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                        arrow={true}
                    >
                        <CustomDropdownButton type="default" className="gap-x-2">
                            <p>{`${startCase(selectedDecompositionLevel?.dataKey)}`}</p>
                            <DownSvg />
                        </CustomDropdownButton>
                    </Dropdown>
                </div>

                <div className="flex flex-row gap-x-2 items-center">
                    <div className="flex justify-end">
                        <span className="flex gap-2">
                            <CopyToClipBoard refOfComponent={chartRef} />
                        </span>
                    </div>
                </div>
            </div>

            {/* Chart */}

            {selectedDecompositionChartType === 'waterfall' ? (
                <DecompositionWaterfallChart />
            ) : (
                <>
                    <div className="items-center flex flex-row justify-between my-2 mb-4">
                        <div className="text-sm">
                            <p>{'Total'}</p>
                        </div>
                    </div>
                    <div className="flex mt-2 flex-row">
                        {/* Y axis */}
                        <div className="flex">
                            <Barchart
                                padding={[0, 0, 15, 0]}
                                barWidth={40}
                                xAxisConfig={true}
                                xAxisLabelFormatter={(val) => {
                                    return val;
                                }}
                                dataset={[]}
                                height={chartHeight}
                                loading={false}
                                chartContainer={`decomposition-chart-y-axis`}
                                fields={['brand_name', 'totalProfit', 'type']}
                                minBound={-300}
                                maxBound={3800000}
                                width={60}
                                colorKey={'type'}
                                yAxisConfig={true}
                                YGrid={false}
                                yAxisLabelFormatter={(val: any) => {
                                    return formatNumWithKMBAlt(val);
                                }}
                            />
                        </div>

                        {/* Main Chart */}
                        <HorizontalInfiniteScroller
                            id={`decomposition-chart-infinite-scroll`}
                            hasMore={true}
                            scrollThreshold={0.9}
                            toScrollWidth={0}
                            onNext={(width: number) => {
                                !isFetchingNextPage && fetchNextPage();
                            }}
                        >
                            <div
                                className="flex flex-1 max-w-full overflow-x-auto"
                                id={`decomposition-chart-infinite-scroll`}
                            >
                                <Barchart
                                    padding={[0, 0, 0, 0]}
                                    barWidth={40}
                                    xAxisConfig={true}
                                    xAxisLabelFormatter={(val) => {
                                        return val;
                                    }}
                                    dataset={decompositionChartData}
                                    height={chartHeight}
                                    loading={isFetchingNextPage}
                                    chartContainer={`decomposition-chart-main`}
                                    fields={['attributeDesc', 'profit_growth_absolute']}
                                    minBound={min}
                                    maxBound={scaleMaxValue(40, max)}
                                    width={Math.max(
                                        graphViewContainerWidth * (decompositionChartData?.length / 10 || 1),
                                        graphViewContainerWidth,
                                    )}
                                    showLoader={false}
                                    colorKey={'colorKey'}
                                    yAxisConfig={true}
                                />
                            </div>
                        </HorizontalInfiniteScroller>
                    </div>
                </>
            )}
        </>
    );
};

export default DecompositionChart;
